/**
 * ============================================================================
 * LiquidacionExcepcionDialog — Barrera 1: Firma de Excepción
 * ============================================================================
 *
 * Cuando el motor de reconciliación detecta un mismatch de pesos > tolerancia,
 * el usuario NO puede liquidar silenciosamente. Debe firmar una excepción con
 * un motivo escrito (>=20 chars) que queda registrado de forma inmutable en
 * `excepciones_liquidacion` (vinculado al snapshot de landed cost).
 *
 * Compliance DIAN: trazabilidad de decisiones humanas en GIGO operativo.
 * ============================================================================
 */
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { WeightReconciliationResult } from "@/lib/weightReconciliation";

const MIN_MOTIVO_LENGTH = 20;

export interface ExcepcionPayload {
  motivo: string;
  delta_kg: number;
  delta_pct: number;
  header_kg: number;
  items_kg: number;
  source: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weightRecon: WeightReconciliationResult;
  isPending?: boolean;
  /** Triggered with the signed exception when the user confirms. */
  onConfirm: (excepcion: ExcepcionPayload) => void | Promise<void>;
}

export function LiquidacionExcepcionDialog({
  open,
  onOpenChange,
  weightRecon,
  isPending,
  onConfirm,
}: Props) {
  const [motivo, setMotivo] = useState("");

  const trimmed = motivo.trim();
  const isValid = trimmed.length >= MIN_MOTIVO_LENGTH;

  const handleConfirm = async () => {
    if (!isValid) return;
    await onConfirm({
      motivo: trimmed,
      delta_kg: weightRecon.deltaKg,
      delta_pct: weightRecon.deltaPct,
      header_kg: weightRecon.headerKg,
      items_kg: weightRecon.itemsKg,
      source: weightRecon.source,
    });
    setMotivo("");
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setMotivo(""); }}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Liquidar con Excepción — Firma requerida
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <Alert className="border-amber-500/50 bg-amber-500/10">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  Discrepancia de peso detectada ({weightRecon.deltaPct.toFixed(2)}%)
                </AlertTitle>
                <AlertDescription className="text-[11px] leading-snug text-amber-800/90 dark:text-amber-300/90 space-y-0.5">
                  <div>
                    Cabecera (
                    {weightRecon.source === "override"
                      ? "override embarque"
                      : weightRecon.source === "documents"
                        ? "suma de documentos"
                        : "—"}
                    ): <strong className="tabular-nums">{weightRecon.headerKg.toLocaleString("es-CO")} kg</strong>
                  </div>
                  <div>
                    Suma de ítems:{" "}
                    <strong className="tabular-nums">{weightRecon.itemsKg.toLocaleString("es-CO")} kg</strong>
                  </div>
                  <div>
                    Δ: <strong className="tabular-nums">{weightRecon.deltaKg.toLocaleString("es-CO")} kg</strong>
                  </div>
                </AlertDescription>
              </Alert>

              <p className="text-sm text-foreground">
                Vas a congelar la liquidación <strong>a pesar</strong> de la discrepancia. Compliance
                DIAN exige que documentes el motivo. La excepción quedará firmada de forma{" "}
                <strong>inmutable</strong> en el registro de auditoría.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="excepcion-motivo" className="text-xs flex items-center gap-2">
            Motivo de la excepción
            <Badge
              variant="outline"
              className={`h-4 px-1 text-[9px] ${
                isValid
                  ? "border-emerald-500/60 text-emerald-700 dark:text-emerald-400"
                  : "border-amber-500/60 text-amber-700 dark:text-amber-400"
              }`}
            >
              {trimmed.length}/{MIN_MOTIVO_LENGTH}+ caracteres
            </Badge>
          </Label>
          <Textarea
            id="excepcion-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej.: Diferencia atribuible a peso del empaque secundario no incluido en items COGS. Validado contra BL #..."
            className="min-h-[100px] text-sm resize-none"
            disabled={isPending}
          />
          <p className="text-[10px] text-muted-foreground leading-snug">
            Mínimo {MIN_MOTIVO_LENGTH} caracteres. Este texto será visible en auditorías DIAN.
          </p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={!isValid || isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isPending && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
            Firmar y Liquidar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
